import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaCheck,
  FaEdit,
  FaEllipsisV,
  FaLock,
  FaPrint,
  FaSearch,
  FaShoppingBag,
  FaTimes,
} from 'react-icons/fa';
import * as orderService from '../../services/order.service';
import { countryName } from '../../components/BuyerLedger/buyerLedgerCountries';
import PageHeader from '../../components/PageHeader/PageHeader';
import OrderProductionSummary from '../../components/OrderProductionSummary/OrderProductionSummary';
import { useAuth } from '../../context/AuthContext';
import '../ClientCatalog/ClientCatalog.css';
import './OrderSearch.css';

const PER_PAGE = 30;
const INITIAL_MODAL_STATE = { open: false, order: null, action: 'authorize' };
const EMPTY_FILTERS = { search: '', fromDate: '', toDate: '' };

const isAuthorized = (status) => (status || '').toLowerCase() === 'authorized';
const fmtDate = (d) => (d ? d : '—');
const fmtQty = (n) => Number(n || 0).toLocaleString();

// An authorized order is locked: its product details cannot be edited until it is un-authorized.
const LOCKED_MESSAGE = 'Product details are locked for editing once an order has been authorized.';

/**
 * Self-contained actions dropdown (no Bootstrap JS dependency — the project loads Bootstrap CSS only).
 * Closes on outside click / Escape.
 */
const ActionsMenu = ({ authorized, onAuthorizeToggle, onEdit, canAuthorize }) => {
  const [open, setOpen] = useState(false);
  // Fixed viewport coordinates so the menu escapes the table card's `overflow: hidden`.
  const [coords, setCoords] = useState({ top: 0, right: 0 });
  const ref = useRef(null);
  const btnRef = useRef(null);

  const place = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCoords({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    // Close on scroll/resize rather than chase the anchor — keeps the menu from detaching.
    const onReflow = () => setOpen(false);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open, place]);

  return (
    <div className="order-actions-menu" ref={ref}>
      <button
        ref={btnRef}
        type="button"
        className="btn btn-sm btn-outline-secondary order-actions-toggle"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <FaEllipsisV />
      </button>
      {open && (
        <div
          className="order-actions-dropdown"
          role="menu"
          style={{ position: 'fixed', top: coords.top, right: coords.right }}
        >
          {canAuthorize && (
            <button
              type="button"
              className={`order-actions-item ${authorized ? 'text-danger' : 'text-success'}`}
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onAuthorizeToggle();
              }}
            >
              {authorized ? <FaTimes /> : <FaCheck />}
              <span>{authorized ? 'Unauthorize' : 'Authorize'}</span>
            </button>
          )}
          {authorized ? (
            <span className="order-actions-item disabled" role="menuitem" aria-disabled="true" title={LOCKED_MESSAGE}>
              <FaLock />
              <span>Edit locked</span>
            </span>
          ) : (
            <button
              type="button"
              className="order-actions-item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onEdit();
              }}
            >
              <FaEdit />
              <span>Edit</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const OrderSearch = () => {
  const navigate = useNavigate();
  const { hasModuleAccess } = useAuth();
  const canAuthorize = hasModuleAccess('authorize_action');

  // Draft filter inputs (typing here does NOT hit the API)...
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  // ...only these applied filters do, alongside `page`.
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);

  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, per_page: PER_PAGE, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(INITIAL_MODAL_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printState, setPrintState] = useState(null); // { rows, at, filters } — drives the print view
  const [summaryOrder, setSummaryOrder] = useState(null); // order row shown in the production summary

  // Single fetch path: fires only when `page` or the applied `filters` change (i.e. on Search / paging).
  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data, pagination: meta } = await orderService.getOrdersPaged({
        page,
        perPage: PER_PAGE,
        search: filters.search,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      });
      setOrders(data);
      setPagination(meta);
    } catch (error) {
      console.error('Failed to load orders', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const applySearch = useCallback(() => {
    setPage(1); // reset to first page whenever a new filter set is applied
    setFilters({ ...draft });
  }, [draft]);

  const clearFilters = useCallback(() => {
    setDraft(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(1);
  }, []);

  const goToPage = useCallback(
    (next) => {
      setPage((prev) => {
        const target = Math.min(Math.max(1, next), pagination.last_page || 1);
        return target === prev ? prev : target;
      });
    },
    [pagination.last_page],
  );

  // Print: pull EVERY order matching the current filters (paging through 200 at a time in the
  // background), then hand a self-contained print view to the browser's "Save as PDF".
  const handlePrint = useCallback(async () => {
    setPrinting(true);
    try {
      const perPage = 200;
      let pageN = 1;
      let rows = [];
      let total = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { data, pagination: meta } = await orderService.getOrdersPaged({
          page: pageN,
          perPage,
          search: filters.search,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
        });
        rows = rows.concat(data);
        total = meta.total || rows.length;
        if (data.length === 0 || rows.length >= total || pageN >= (meta.last_page || 1)) break;
        pageN += 1;
      }
      setPrintState({ rows, at: new Date().toLocaleString(), filters: { ...filters } });
    } catch (error) {
      console.error('Failed to prepare order list for printing', error);
      alert('Failed to prepare the order list for printing.');
    } finally {
      setPrinting(false);
    }
  }, [filters]);

  // Fire the print dialog once the print view has rendered; clear it when the dialog closes.
  useEffect(() => {
    if (!printState) return undefined;
    const done = () => setPrintState(null);
    window.addEventListener('afterprint', done);
    window.print();
    return () => window.removeEventListener('afterprint', done);
  }, [printState]);

  const confirmAuthorize = useCallback(async () => {
    if (!modal.order?.id) return;
    setSubmitting(true);
    try {
      const nextStatus = modal.action === 'authorize' ? 'authorized' : 'pending';
      const updated = await orderService.updateOrderAuthorization(modal.order.id, nextStatus);
      setOrders((prev) =>
        prev.map((o) =>
          o.id === modal.order.id
            ? { ...o, status: updated?.status ?? nextStatus, merchant: updated?.merchant ?? (nextStatus === 'authorized' ? o.merchant : '—') }
            : o,
        ),
      );
      setModal(INITIAL_MODAL_STATE);
    } catch (error) {
      console.error('Failed to change order authorization', error);
      alert('Failed to update order authorization status');
    } finally {
      setSubmitting(false);
    }
  }, [modal]);

  // Serial number continues across pages.
  const rowOffset = (pagination.page - 1) * pagination.per_page;
  const hasFilters = Boolean(filters.search || filters.fromDate || filters.toDate);

  const pageNumbers = useMemo(() => {
    const last = pagination.last_page || 1;
    const current = pagination.page || 1;
    // Windowed page list so a large order set doesn't render hundreds of buttons.
    const start = Math.max(1, current - 2);
    const end = Math.min(last, start + 4);
    const realStart = Math.max(1, end - 4);
    const nums = [];
    for (let i = realStart; i <= end; i += 1) nums.push(i);
    return nums;
  }, [pagination.last_page, pagination.page]);

  return (
    <div className="client-catalog-container authorization-page" style={{ minHeight: '100vh' }}>
      <PageHeader
        icon={<FaShoppingBag size={22} />}
        title="Order Search"
        subtitle="Search orders, review their status, and authorize or edit them"
        actions={(
          <button
            type="button"
            className="btn btn-light order-print-btn"
            onClick={handlePrint}
            disabled={printing || loading || pagination.total === 0}
            title="Print / save the filtered order list as PDF"
          >
            <FaPrint className="me-2" />
            {printing ? 'Preparing…' : 'Print / PDF'}
          </button>
        )}
      />

      <div className="client-catalog-main">
        <section className="catalog-content-area-full">
          {/* Filter bar — nothing here queries the API until "Search" is clicked. */}
          <form
            className="authorization-toolbar"
            onSubmit={(e) => {
              e.preventDefault();
              applySearch();
            }}
          >
            <label className="authorization-search">
              <FaSearch className="authorization-search-icon" />
              <input
                type="text"
                placeholder="Search anything — order no., buyer, PO, country, merchant..."
                value={draft.search}
                onChange={(e) => setDraft((d) => ({ ...d, search: e.target.value }))}
              />
            </label>

            <label className="authorization-filter order-search-date">
              <span className="order-search-date-label">Date From</span>
              <input
                type="date"
                value={draft.fromDate}
                max={draft.toDate || undefined}
                onChange={(e) => setDraft((d) => ({ ...d, fromDate: e.target.value }))}
              />
            </label>

            <label className="authorization-filter order-search-date">
              <span className="order-search-date-label">Date To</span>
              <input
                type="date"
                value={draft.toDate}
                min={draft.fromDate || undefined}
                onChange={(e) => setDraft((d) => ({ ...d, toDate: e.target.value }))}
              />
            </label>

            <button type="submit" className="btn btn-primary">
              <FaSearch className="me-1" /> Search
            </button>
            {(hasFilters || draft.search || draft.fromDate || draft.toDate) && (
              <button type="button" className="btn btn-outline-secondary" onClick={clearFilters}>
                Clear
              </button>
            )}
          </form>

          {loading ? (
            <div className="empty-state authorization-empty-state">
              <p>Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="empty-state authorization-empty-state">
              <FaShoppingBag size={48} className="text-muted mb-3" />
              <h5>No matching orders</h5>
              <p className="text-muted">
                {hasFilters ? 'Try adjusting the search term or date range.' : 'No orders have been created yet.'}
              </p>
            </div>
          ) : (
            <>
              <div className="authorization-table-card">
                <div className="table-responsive">
                  <table className="table authorization-table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Sr. No.</th>
                        <th>Order No.</th>
                        <th>Buyer PO No.</th>
                        <th>Order Rec. Date</th>
                        <th>Ex Factory Date</th>
                        <th>Order Qty</th>
                        <th>Buyer Name</th>
                        <th>Country</th>
                        <th>Status</th>
                        <th>Merchant</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order, index) => {
                        const authorized = isAuthorized(order.status);
                        return (
                          <tr key={order.id}>
                            <td>{rowOffset + index + 1}</td>
                            <td>
                              {order.orderNo ? (
                                <button
                                  type="button"
                                  className="order-no-link"
                                  onClick={() => setSummaryOrder(order)}
                                  title="View production summary"
                                >
                                  {order.orderNo}
                                </button>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td>{order.buyerPo || '—'}</td>
                            <td>{fmtDate(order.recDate)}</td>
                            <td>{fmtDate(order.exFactoryDate)}</td>
                            <td>{fmtQty(order.orderQty)}</td>
                            <td>{order.buyerName || '—'}</td>
                            <td>{countryName(order.country) || '—'}</td>
                            <td>
                              <span className={`authorization-status-pill ${authorized ? 'approved' : 'unapproved'}`}>
                                {authorized ? 'Authorized' : 'Pending'}
                              </span>
                            </td>
                            <td>{order.merchant || '—'}</td>
                            <td className="text-center">
                              <ActionsMenu
                                authorized={authorized}
                                canAuthorize={canAuthorize}
                                onAuthorizeToggle={() =>
                                  setModal({
                                    open: true,
                                    order,
                                    action: authorized ? 'unauthorize' : 'authorize',
                                  })
                                }
                                onEdit={() => navigate(`/dashboard/order/edit/${order.id}`)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="order-search-pager">
                <small className="text-muted">
                  Showing {rowOffset + 1}–{rowOffset + orders.length} of {pagination.total} orders
                </small>
                <nav aria-label="Order pages">
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${pagination.page <= 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => goToPage(pagination.page - 1)} disabled={pagination.page <= 1}>
                        Previous
                      </button>
                    </li>
                    {pageNumbers[0] > 1 && (
                      <li className="page-item disabled">
                        <span className="page-link">…</span>
                      </li>
                    )}
                    {pageNumbers.map((n) => (
                      <li key={n} className={`page-item ${n === pagination.page ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => goToPage(n)}>
                          {n}
                        </button>
                      </li>
                    ))}
                    {pageNumbers[pageNumbers.length - 1] < pagination.last_page && (
                      <li className="page-item disabled">
                        <span className="page-link">…</span>
                      </li>
                    )}
                    <li className={`page-item ${pagination.page >= pagination.last_page ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => goToPage(pagination.page + 1)}
                        disabled={pagination.page >= pagination.last_page}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </>
          )}
        </section>
      </div>

      {/* Print-only view — the full filtered list, shown solely by the @media print stylesheet. */}
      {printState && (
        <div className="order-print-area">
          <div className="order-print-head">
            <h2>Order List</h2>
            <div className="order-print-meta">
              <span>Generated: {printState.at}</span>
              {printState.filters.search && <span>Search: “{printState.filters.search}”</span>}
              {(printState.filters.fromDate || printState.filters.toDate) && (
                <span>
                  Rec. Date: {printState.filters.fromDate || '…'} → {printState.filters.toDate || '…'}
                </span>
              )}
              <span>Total orders: {printState.rows.length}</span>
            </div>
          </div>
          <table className="order-print-table">
            <thead>
              <tr>
                <th>Sr. No.</th>
                <th>Order No.</th>
                <th>Buyer PO No.</th>
                <th>Order Rec. Date</th>
                <th>Ex Factory Date</th>
                <th>Order Qty</th>
                <th>Buyer Name</th>
                <th>Country</th>
                <th>Status</th>
                <th>Merchant</th>
              </tr>
            </thead>
            <tbody>
              {printState.rows.map((o, i) => (
                <tr key={o.id}>
                  <td>{i + 1}</td>
                  <td>{o.orderNo || '—'}</td>
                  <td>{o.buyerPo || '—'}</td>
                  <td>{fmtDate(o.recDate)}</td>
                  <td>{fmtDate(o.exFactoryDate)}</td>
                  <td>{fmtQty(o.orderQty)}</td>
                  <td>{o.buyerName || '—'}</td>
                  <td>{countryName(o.country) || '—'}</td>
                  <td>{isAuthorized(o.status) ? 'Authorized' : 'Pending'}</td>
                  <td>{o.merchant || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {summaryOrder && (
        <OrderProductionSummary order={summaryOrder} onClose={() => setSummaryOrder(null)} />
      )}

      {modal.open && modal.order && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="import-modal" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <div>
                <h5>{modal.action === 'authorize' ? 'Authorize order' : 'Unauthorize order'}</h5>
                <p className="text-muted mb-0">
                  {modal.order.orderNo} • {modal.order.buyerName}
                </p>
              </div>
              <button className="btn-close-client" onClick={() => setModal(INITIAL_MODAL_STATE)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-body" style={{ padding: '1.5rem' }}>
              <ul className="list-group">
                <li className="list-group-item"><strong>Order No.:</strong> {modal.order.orderNo || '—'}</li>
                <li className="list-group-item"><strong>Buyer:</strong> {modal.order.buyerName || '—'}</li>
                <li className="list-group-item"><strong>Buyer PO No.:</strong> {modal.order.buyerPo || '—'}</li>
                <li className="list-group-item"><strong>Order Rec. Date:</strong> {fmtDate(modal.order.recDate)}</li>
                <li className="list-group-item"><strong>Order Qty:</strong> {fmtQty(modal.order.orderQty)}</li>
                <li className="list-group-item"><strong>Country:</strong> {countryName(modal.order.country) || '—'}</li>
              </ul>
              <p className="text-muted mt-3 mb-0">
                {modal.action === 'authorize'
                  ? 'Authorizing this order will lock its product details from further editing.'
                  : 'Unauthorizing this order will unlock its product details for editing.'}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={() => setModal(INITIAL_MODAL_STATE)}>
                Cancel
              </button>
              <button
                className={modal.action === 'authorize' ? 'btn btn-success' : 'btn btn-danger'}
                onClick={confirmAuthorize}
                disabled={submitting}
              >
                {submitting ? 'Updating...' : modal.action === 'authorize' ? 'Authorize' : 'Unauthorize'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderSearch;
